import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Tooltip,
  Switch,
  FormControlLabel,
  LinearProgress,
  Drawer,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemIcon,
  ListItemText as MuiListItemText,
  Divider,
  CardHeader,
  IconButton as MuiIconButton,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Error,
  Code,
  Build,
  Description,
  Settings,
  AccountBalance,
  Monitor,
  Menu,
  ChevronLeft,
  Home,
  BarChart,
  Message,
  Engineering,
  Receipt,
  Tune,
  AttachMoney,
  Memory,
  Save,
  Add,
  SmartToy,
  Dashboard,
  Search,
  GppGood,
  PlayArrow,
  Stop,
  Schedule,
} from '@mui/icons-material';
import mcpService from '../../services/mcpService.js';
import { pythonClient } from '../../api/axios.client';
import connectionTimeoutService from '../../services/connectionTimeoutService.js';
import errorRecoveryService from '../../services/errorRecoveryService.js';
import MCP_API from '../../api/mcp.api.js';
import ConnectionStatusIndicator from '../Connection/ConnectionStatusIndicator.jsx';

function MCPDashboard() {
  const location = useLocation();

  // ===== HOOKS MUST BE DECLARED FIRST =====
  // Data state
  const [serverStatus, setServerStatus] = useState(null);
  const [flows, setFlows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tools, setTools] = useState([]);
  const [aiModels, setAiModels] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [storageStats, setStorageStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  // Connection and robustness state
  const [connectionStates, setConnectionStates] = useState({});
  const [serviceAvailability, setServiceAvailability] = useState({});
  const [connectionMetrics, setConnectionMetrics] = useState({});
  const [errorRecoveryStats, setErrorRecoveryStats] = useState({});
  const [rateLimitStatus, setRateLimitStatus] = useState({});
  const [healthStatus, setHealthStatus] = useState({});

  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const mcpServiceRef = useRef(null);

  // ===== LOGIC AFTER HOOKS =====
  // Determine active section based on current path
  const getActiveSection = () => {
    const path = location.pathname;
    if (path === '/mcp-dashboard') return 'overview';
    if (path === '/mcp-dashboard/ai' || path === '/mcp-analytics') return 'ai';
    if (path === '/mcp-dashboard/system') return 'system';
    if (path === '/mcp-dashboard/billing') return 'billing';
    if (path === '/mcp-dashboard/monitoring') return 'monitoring';
    if (path === '/mcp-dashboard/settings') return 'settings';
    if (path === '/mcp-templates') return 'templates';
    if (path === '/mcp-tools') return 'tools';
    if (path === '/mcp-search') return 'search';
    if (path === '/mcp-statistics') return 'statistics';
    return 'overview'; // default
  };

  const activeSection = getActiveSection();

  // Section titles
  const getSectionTitle = () => {
    switch (activeSection) {
      case 'overview': return 'MCP Server Control';
      case 'ai': return 'AI Model Management';
      case 'system': return 'Flow Management';
      case 'billing': return 'MCP Billing & Costs';
      case 'monitoring': return 'System Monitoring';
      case 'settings': return 'MCP Settings';
      case 'templates': return 'Flow Templates';
      case 'tools': return 'MCP Tools';
      case 'search': return 'Flow Search';
      case 'statistics': return 'Flow Statistics';
      default: return 'MCP Dashboard';
    }
  };

  // Initialize connection monitoring
  useEffect(() => {
    let isMounted = true;

    const initAndCleanup = async () => {
      try {
        console.log('🔧 DASHBOARD: Starting MCP service initialization...');
        // Initialize MCP service with error recovery
        mcpServiceRef.current = await errorRecoveryService.executeWithRecovery(
          async () => {
            console.log('🔧 DASHBOARD: Calling mcpService.initialize()...');
            const result = await mcpService.initialize();
            console.log('🔧 DASHBOARD: mcpService.initialize() completed');
            return result;
          },
          { config: { maxAttempts: 3 } },
        );
        console.log('🔧 DASHBOARD: MCP service initialized successfully');

        if (isMounted) {
          // Load initial data with timeout handling
          await loadServerStatus();
          await loadAIModels();
          await loadAIStats();
          await loadStorageStats();
          await loadFlows();
          await loadTemplates();
          await loadTools();

          // Initialize connection monitoring
          await initializeConnectionMonitoring();
        }
      } catch (error) {
        console.error('Failed to initialize MCP:', error);
        if (isMounted) {
          showSnackbar('Failed to initialize MCP service', 'error');
        }
      }
    };

    initAndCleanup();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      connectionTimeoutService.cleanup();
      errorRecoveryService.clearErrorHistory();
    };
  }, []);

  // Monitor connection states
  useEffect(() => {
    const handleStateChange = (data) => {
      setConnectionStates((prev) => ({
        ...prev,
        [data.service]: { state: data.state, timestamp: Date.now(), data: data.data },
      }));
    };

    const handleNetworkRecovered = (data) => {
      setServiceAvailability(data);
      showSnackbar('Network connection restored', 'success');
    };

    const handleAuthRequired = () => {
      showSnackbar('Authentication required. Please log in again.', 'warning');
    };

    connectionTimeoutService.on('state_change', handleStateChange);
    connectionTimeoutService.on('network_recovered', handleNetworkRecovered);
    errorRecoveryService.on('auth_required', handleAuthRequired);

    // Initial state load
    setConnectionStates(connectionTimeoutService.getConnectionStates());
    setConnectionMetrics(connectionTimeoutService.getMetrics());
    setErrorRecoveryStats(errorRecoveryService.getRecoveryStats());

    return () => {
      connectionTimeoutService.off('state_change', handleStateChange);
      connectionTimeoutService.off('network_recovered', handleNetworkRecovered);
      errorRecoveryService.off('auth_required', handleAuthRequired);
    };
  }, []);

  // Periodic service availability check
  useEffect(() => {
    const checkAvailability = async () => {
      try {
        const availability = await connectionTimeoutService.checkServiceAvailability('all', 5000);
        setServiceAvailability(availability);

        // Check rate limiting status
        const rateLimitInfo = await MCP_API.getServerStatus();
        setRateLimitStatus(rateLimitInfo?.rate_limit_info || {});

        // Get health status
        const healthData = await MCP_API.checkAvailability();
        setHealthStatus(healthData);

        // Check WebSocket connection status - use MCP_API for more reliable check
        console.log('🔗 CHECKING WEBSOCKET STATUS...');
        console.log('🔗 MCP Service wsConnected:', mcpService.wsConnected);
        console.log('🔗 MCP API isWebSocketConnected:', MCP_API.isWebSocketConnected());
        setWsConnected(MCP_API.isWebSocketConnected());
      } catch (error) {
        console.warn('Failed to check service availability:', error);
        setWsConnected(false);
      }
    };

    // Initial check
    checkAvailability();

    // Set up periodic checks (reduced frequency to avoid excessive requests)
    const interval = setInterval(checkAvailability, 60000); // Check every 60 seconds instead of 30

    return () => clearInterval(interval);
  }, []);

  // Initialize connection monitoring
  const initializeConnectionMonitoring = async () => {
    try {
      // Wait for connection to be established
      await connectionTimeoutService.executeWithTimeout('/api/v1/mcp/status', { method: 'GET' }, {
        timeout: 5000,
        retries: 3,
      });

      console.log('✅ MCP Dashboard connection monitoring initialized');
    } catch (error) {
      console.warn('⚠️ Failed to initialize connection monitoring:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleServerControl = async () => {
    try {
      setLoading(true);

      if (serverStatus?.running) {
        // Stop server
        const result = await pythonClient.post('/mcp/server/stop');

        // Check if response exists
        if (!result) {
          showSnackbar('Failed to stop MCP server: No response from server', 'error');
          return;
        }

        if (result.success) {
          showSnackbar('MCP server stopped successfully', 'success');
          await loadServerStatus(); // Refresh status
        } else {
          // Check if this is the expected response for in-process server
          if (result.message?.includes('in-process') || result.note?.includes('in-process')) {
            showSnackbar('MCP server is running in-process mode and cannot be stopped', 'info');
          } else {
            showSnackbar(result.message || 'Failed to stop MCP server', 'error');
          }
        }
      } else {
        // Start server
        const result = await pythonClient.post('/mcp/server/start');

        // Check if response exists
        if (!result) {
          showSnackbar('Failed to start MCP server: No response from server', 'error');
          return;
        }

        if (result.success) {
          showSnackbar('MCP server started successfully', 'success');
          await loadServerStatus(); // Refresh status
        } else {
          showSnackbar(result.message || 'Failed to start MCP server', 'error');
        }
      }
    } catch (error) {
      console.error('Server control error:', error);
      const action = serverStatus?.running ? 'stop' : 'start';

      // Handle different types of errors
      let errorMessage = `Failed to ${action} MCP server`;
      if (error.response) {
        // Server responded with error status
        errorMessage += `: ${error.response.status} ${error.response.statusText}`;
        if (error.response.data && error.response.data.detail) {
          errorMessage += ` - ${error.response.data.detail}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage += ': Network error - unable to reach server';
      } else {
        // Other error
        errorMessage += `: ${error.message}`;
      }

      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenServerSettings = () => {
    // Navigate to server settings section
    // For now, we'll scroll to the settings section if it exists
    const settingsSection = document.getElementById('server-settings');
    if (settingsSection) {
      settingsSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If no settings section exists, show a message
      showSnackbar('Server settings not available in this view', 'info');
    }
  };

  // Data loading functions
  const loadServerStatus = async () => {
    try {
      const result = await pythonClient.get('/mcp/status');
      if (result) {
        setServerStatus(result);
      } else {
        console.error('Failed to load server status: No response');
        setServerStatus(null);
      }
    } catch (error) {
      console.error('Failed to load server status:', error);
      setServerStatus(null);
    }
  };

  const loadAIModels = async () => {
    try {
      const result = await pythonClient.get('/mcp/ai/models');
      if (result) {
        setAiModels(result);
      } else {
        console.error('Failed to load AI models: No response');
        setAiModels(null);
      }
    } catch (error) {
      console.error('Failed to load AI models:', error);
      setAiModels(null);
    }
  };

  const loadAIStats = async () => {
    try {
      const result = await pythonClient.get('/mcp/ai/stats');
      if (result) {
        setAiStats(result);
      } else {
        console.error('Failed to load AI stats: No response');
        setAiStats(null);
      }
    } catch (error) {
      console.error('Failed to load AI stats:', error);
      setAiStats(null);
    }
  };

  const loadStorageStats = async () => {
    try {
      setLoading(true);
      const result = await pythonClient.get('/mcp/storage/stats');

      // Handle different response structures
      let storageData = {};
      if (result) {
        if (result.storage_stats) {
          storageData = result.storage_stats;
        } else if (typeof result === 'object') {
          // Fallback: use the entire response as storage stats
          storageData = result;
        }
      }

      setStorageStats(storageData);
    } catch (error) {
      console.error('Failed to load storage stats:', error);
      showSnackbar('Failed to load storage statistics', 'error');
      // Set empty stats on error to prevent UI crashes
      setStorageStats({});
    } finally {
      setLoading(false);
    }
  };

  const loadFlows = async () => {
    try {
      const result = await pythonClient.get('/mcp/flows');
      setFlows(result || []);
    } catch (error) {
      console.error('Failed to load flows:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const result = await pythonClient.get('/mcp/templates');
      setTemplates(result || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadTools = async () => {
    try {
      const result = await pythonClient.get('/mcp/tools');
      setTools(result || []);
    } catch (error) {
      console.error('Failed to load tools:', error);
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return <DashboardSection />;
      case 'ai':
        return <AIManagementSection />;
      case 'system':
        return <SystemManagementSection />;
      case 'billing':
        return <BillingSection />;
      case 'monitoring':
        return <MonitoringSection />;
      case 'settings':
        return <SettingsSection />;
      case 'templates':
        return <TemplatesSection />;
      case 'tools':
        return <ToolsSection />;
      case 'search':
        return <SearchSection />;
      case 'statistics':
        return <StatisticsSection />;
      default:
        return <DashboardSection />;
    }
  };

  // ===== SECTION COMPONENTS =====

  function DashboardSection() {
    return (
      <Box>
        {/* Connection Status Indicator */}
        <ConnectionStatusIndicator showDetails />

        {/* Server Control */}
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                MCP Server Control
              </Typography>
              <Box>
                <IconButton onClick={loadServerStatus} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
              <Badge
                color={serverStatus?.running ? 'success' : 'error'}
                variant="dot"
                sx={{ mr: 1 }}
              >
                <Monitor sx={{ fontSize: 24 }} />
              </Badge>
              <Box>
                <Typography variant="subtitle1">
                  Server Status:
                  {' '}
                  {serverStatus?.running ? 'Running' : 'Stopped'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Real-time WebSocket:
                  {' '}
                  {wsConnected ? '🟢 Connected' : '🔴 Disconnected'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                variant={serverStatus?.running ? 'outlined' : 'contained'}
                color={serverStatus?.running ? 'error' : 'success'}
                startIcon={serverStatus?.running ? <Stop /> : <PlayArrow />}
                onClick={handleServerControl}
                disabled={loading}
                sx={{ mr: 2 }}
              >
                {serverStatus?.running ? 'Stop Server' : 'Start Server'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOpenServerSettings}
              >
                Server Settings
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Key Metrics Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <SmartToy sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {aiModels?.available_models ? Object.keys(aiModels.available_models).length : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  AI Providers
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <AttachMoney sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" sx={{ mb: 1 }}>
                  $
                  {aiStats?.ai_statistics?.total_cost_usd?.toFixed(2) || '0.00'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Cost
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Build sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {flows?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Flows
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Monitor sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {wsConnected ? 'Online' : 'Offline'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  System Status
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader
                title="Recent AI Activity"
                avatar={<BarChart />}
              />
              <CardContent>
                {aiStats?.ai_statistics?.providers ? (
                  <Box>
                    {Object.entries(aiStats.ai_statistics.providers).map(([provider, data]) => (
                      <Box
                        key={provider}
                        sx={{
                          mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {provider.toUpperCase()}
                        </Typography>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">
                            Calls:
                            {data.calls}
                          </Typography>
                          <Typography variant="body2">
                            Cost: $
                            {((data.tokens || 0) / 1000 * 0.002).toFixed(4)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No recent AI activity
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader
                title="System Health"
                avatar={<Monitor />}
              />
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <Badge color={wsConnected ? 'success' : 'error'} variant="dot">
                    <Typography variant="body2">WebSocket</Typography>
                  </Badge>
                  <Typography variant="caption" color="text.secondary">
                    {wsConnected ? 'Connected' : 'Disconnected'}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <Badge color={serverStatus?.running ? 'success' : 'error'} variant="dot">
                    <Typography variant="body2">MCP Server</Typography>
                  </Badge>
                  <Typography variant="caption" color="text.secondary">
                    {serverStatus?.running ? 'Running' : 'Stopped'}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2}>
                  <Badge color="success" variant="dot">
                    <Typography variant="body2">Database</Typography>
                  </Badge>
                  <Typography variant="caption" color="text.secondary">
                    Connected
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Robustness & Performance Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title="System Robustness & Performance"
                avatar={<GppGood />}
                action={(
                  <Button
                    startIcon={<Refresh />}
                    onClick={() => {
                      setConnectionMetrics(connectionTimeoutService.getMetrics());
                      setErrorRecoveryStats(errorRecoveryService.getRecoveryStats());
                    }}
                    size="small"
                  >
                    Refresh
                  </Button>
              )}
              />
              <CardContent>
                <Grid container spacing={2}>
                  {/* Connection States */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Connection States</Typography>
                    <Box sx={{
                      display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2,
                    }}
                    >
                      {Object.entries(connectionStates).map(([service, state]) => (
                        <Chip
                          key={service}
                          icon={
                          state.state === 'connected' || state.state === 'available' ? <CheckCircle sx={{ fontSize: 16 }} />
                            : state.state === 'disconnected' || state.state === 'unavailable' ? <Error sx={{ fontSize: 16 }} />
                              : <Schedule sx={{ fontSize: 16 }} />
                        }
                          label={`${service}: ${state.state}`}
                          color={
                          state.state === 'connected' || state.state === 'available' ? 'success'
                            : state.state === 'disconnected' || state.state === 'unavailable' ? 'error'
                              : 'warning'
                        }
                          size="small"
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Service Availability */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Service Availability</Typography>
                    <Box sx={{
                      display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2,
                    }}
                    >
                      {Object.entries(serviceAvailability).map(([service, status]) => (
                        <Chip
                          key={service}
                          icon={status.available ? <CheckCircle sx={{ fontSize: 16 }} /> : <Error sx={{ fontSize: 16 }} />}
                          label={`${service}: ${status.available ? 'available' : 'unavailable'}`}
                          color={status.available ? 'success' : 'error'}
                          size="small"
                          variant={status.available ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Connection Metrics */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Connection Metrics</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Active Requests:</Typography>
                        <Typography variant="body2">{connectionMetrics.activeRequests || 0}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Success Rate:</Typography>
                        <Typography variant="body2">
                          {connectionMetrics.successCount || 0}
                          {' '}
                          /
                          {connectionMetrics.retryCount || 0 + connectionMetrics.successCount || 0}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Timeouts:</Typography>
                        <Typography variant="body2">{connectionMetrics.requestTimeouts || 0}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Last Sync:</Typography>
                        <Typography variant="body2">
                          {connectionMetrics.lastSyncTime ? new Date(connectionMetrics.lastSyncTime).toLocaleTimeString() : 'Never'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Error Recovery Stats */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Error Recovery</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Total Recoveries:</Typography>
                        <Typography variant="body2">{errorRecoveryStats.totalRecoveries || 0}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Active Recoveries:</Typography>
                        <Typography variant="body2">{errorRecoveryStats.activeRecoveries?.length || 0}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Error History Size:</Typography>
                        <Typography variant="body2">{errorRecoveryStats.errorHistorySize || 0}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Available Strategies:</Typography>
                        <Typography variant="body2">{errorRecoveryStats.recoveryStrategies?.length || 0}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  function AIManagementSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          AI Management Dashboard
        </Typography>

        {/* AI Models Overview */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardHeader
            title="Configured AI Models"
            avatar={<SmartToy />}
            action={(
              <Button startIcon={<Refresh />} onClick={loadAIModels} disabled={loading}>
                Refresh
              </Button>
          )}
          />
          <CardContent>
            {aiModels?.available_models ? (
              <Grid container spacing={2}>
                {Object.entries(aiModels.available_models).map(([provider, models]) => (
                  <Grid item xs={12} md={6} lg={4} key={provider}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            {provider.toUpperCase()}
                          </Typography>
                          <Chip
                            label={aiModels.configured_providers?.includes(provider) ? 'Active' : 'Inactive'}
                            color={aiModels.configured_providers?.includes(provider) ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Available Models:
                        </Typography>
                        <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                          {models.map((model, index) => (
                            <Chip
                              key={index}
                              label={model}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info">
                No AI models configured. Please set up API keys to enable AI functionality.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Usage Analytics */}
        <Card variant="outlined">
          <CardHeader
            title="AI Usage Analytics"
            avatar={<BarChart />}
            action={(
              <Button startIcon={<Refresh />} onClick={loadAIStats} disabled={loading}>
                Refresh
              </Button>
          )}
          />
          <CardContent>
            {aiStats?.ai_statistics ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {aiStats.ai_statistics.total_tokens}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Tokens Used
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      $
                      {aiStats.ai_statistics.total_cost_usd?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Cost
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {aiStats.ai_statistics.total_calls}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      API Calls Made
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No usage data available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  function SystemManagementSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          System Management
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader title="Flow Management" avatar={<Build />} />
              <CardContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Manage your agentic flows and register them as MCP tools.
                </Typography>
                <Box display="flex" gap={1}>
                  <Button variant="outlined" startIcon={<Add />}>
                    Create Flow
                  </Button>
                  <Button variant="outlined" startIcon={<Search />}>
                    Browse Flows
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader title="Template Library" avatar={<Description />} />
              <CardContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Use pre-built templates to quickly create new flows.
                </Typography>
                <Button variant="outlined" startIcon={<Add />}>
                  Browse Templates
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader title="Tool Registry" avatar={<Code />} />
              <CardContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Register and manage MCP tools for external access.
                </Typography>
                <Button variant="outlined" startIcon={<Settings />}>
                  Manage Tools
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardHeader title="Search & Discovery" avatar={<Search />} />
              <CardContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Advanced search across flows, templates, and tools.
                </Typography>
                <Button variant="outlined" startIcon={<Search />}>
                  Advanced Search
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  function BillingSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Billing & Cost Management
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong>
            {' '}
            This is a demonstration of billing features.
            In a production environment, integrate with payment processors like Stripe.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <AccountBalance sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h4">$0.00</Typography>
                <Typography variant="body2" color="text.secondary">Current Month</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Receipt sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h4">$0.00</Typography>
                <Typography variant="body2" color="text.secondary">Last Month</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Engineering sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4">0</Typography>
                <Typography variant="body2" color="text.secondary">Pending Invoices</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardHeader title="Usage Limits" avatar={<AccountBalance />} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Monthly Token Limit</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Used:</Typography>
                  <Typography variant="body2">0 / 100,000</Typography>
                </Box>
                <LinearProgress variant="determinate" value={0} sx={{ height: 8, borderRadius: 4 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Monthly Cost Limit</Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Used:</Typography>
                  <Typography variant="body2">$0.00 / $10.00</Typography>
                </Box>
                <LinearProgress variant="determinate" value={0} sx={{ height: 8, borderRadius: 4 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  function MonitoringSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          System Monitoring & Health
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Monitor sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h4">
                  {wsConnected ? 'Online' : 'Offline'}
                </Typography>
                <Typography variant="body2" color="text.secondary">WebSocket Status</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Memory sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h4">
                  {storageStats?.total_documents || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Documents Stored</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Code sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4">
                  {Object.keys(storageStats || {}).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Collections</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardHeader title="System Health Checks" avatar={<GppGood />} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircle sx={{ color: 'success.main' }} />
                  <Box>
                    <Typography variant="subtitle2">Database Connection</Typography>
                    <Typography variant="body2" color="text.secondary">MongoDB connection is healthy</Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircle sx={{ color: 'success.main' }} />
                  <Box>
                    <Typography variant="subtitle2">WebSocket Service</Typography>
                    <Typography variant="body2" color="text.secondary">Real-time communication active</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  function SettingsSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          MCP Settings & Configuration
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Security Note:</strong>
            {' '}
            API keys are stored in environment variables.
            Never commit them to version control.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>AI Model Configuration</Typography>

                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enable AI Model Integration"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Use Response Caching"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={<Switch />}
                    label="Enable Cost Tracking"
                  />
                </Box>

                <Typography variant="subtitle2" gutterBottom>Default Model Preferences</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="gpt-4, claude-3-sonnet, gpt-3.5-turbo"
                  helperText="Comma-separated list of preferred models"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>API Key Management</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="OpenAI API Key"
                      type="password"
                      placeholder="sk-..."
                      helperText="Starts with 'sk-'"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Anthropic API Key"
                      type="password"
                      placeholder="sk-ant-..."
                      helperText="Starts with 'sk-ant-'"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" startIcon={<Save />}>
                    Save Configuration
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  function TemplatesSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Flow Templates Library
        </Typography>

        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {template.description}
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={template.category}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={template.complexity}
                      size="small"
                      color={template.complexity === 'simple' ? 'success'
                        : template.complexity === 'medium' ? 'warning' : 'error'}
                    />
                  </Box>

                  <Typography variant="body2">
                    {template.nodes?.length || 0}
                    {' '}
                    nodes •
                    {template.edges?.length || 0}
                    {' '}
                    connections
                  </Typography>

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained">
                      Use Template
                    </Button>
                    <Button size="small" variant="outlined">
                      Preview
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  function ToolsSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          MCP Tools Registry
        </Typography>

        <Grid container spacing={3}>
          {tools.map((tool) => (
            <Grid item xs={12} md={6} lg={4} key={tool.name || tool.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {tool.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {tool.description}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    <Button size="small" variant="contained">
                      Execute Tool
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  function SearchSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Advanced Flow Search
        </Typography>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Search Query"
                  placeholder="Search by name, description, or tags..."
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select>
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="business">Business</MenuItem>
                    <MenuItem value="technical">Technical</MenuItem>
                    <MenuItem value="creative">Creative</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Complexity</InputLabel>
                  <Select>
                    <MenuItem value="">All Complexities</MenuItem>
                    <MenuItem value="simple">Simple</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="complex">Complex</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button variant="contained" startIcon={<Search />}>
                  Search Flows
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  function StatisticsSection() {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Flow Execution Statistics
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Build sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4">0</Typography>
                <Typography variant="body2" color="text.secondary">Total Executions</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="h4">0%</Typography>
                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Code sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h4">{flows.length}</Typography>
                <Typography variant="body2" color="text.secondary">Active Flows</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Description sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                <Typography variant="h4">{templates.length}</Typography>
                <Typography variant="body2" color="text.secondary">Available Templates</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Box sx={{
        mb: 3, display: 'flex', alignItems: 'center', gap: 2,
      }}
      >
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {getSectionTitle()}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            loadServerStatus();
            loadAIModels();
            loadAIStats();
            loadStorageStats();
          }}
          disabled={loading}
        >
          Refresh All
        </Button>
      </Box>

      {renderSection()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MCPDashboard;
